/**
 * ZusatzReportingPanel.jsx
 *
 * Gemeinsame Bestandsmeldungs-Komponente (Admin + Employee).
 * Vollständiger Handover-Workflow:
 *   A) Frühschicht + Verkäufe  → Selbst-Bestätigung
 *   B) Kollege + Verkäufe      → 2-Schritt (Geber + Empfänger)
 *   C) Delta = 0               → Einzel-Bestätigung "Nichts verkauft"
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  PackagePlus, Minus, Plus, Save, Loader2, AlertTriangle,
  User, CheckCircle2, Clock, Euro, ArrowRightLeft, Sunrise, X, Check, ChevronDown,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authH = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

// ── Schritt-Modal ─────────────────────────────────────────────────────────────
function StepModal({ title, subtitle, icon: Icon, iconColor = 'text-primary', children, onConfirm, onCancel, confirmLabel = 'Bestätigen', confirmDisabled = false }) {
  return (
    <div className="fixed inset-0 z-[9999] bg-black/60 flex items-center justify-center p-4" onClick={onCancel}>
      <div className="bg-background rounded-2xl shadow-2xl max-w-md w-full p-6 space-y-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center shrink-0">
            <Icon className={`h-5 w-5 ${iconColor}`} />
          </div>
          <div>
            <h3 className="font-bold text-base">{title}</h3>
            {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
          </div>
        </div>
        {children}
        <div className="flex flex-col-reverse sm:flex-row gap-2 pt-1">
          {onCancel && (
            <Button variant="outline" className="sm:flex-1" onClick={onCancel}>
              <X className="h-4 w-4 mr-1.5" /> Abbrechen
            </Button>
          )}
          <Button className="sm:flex-1" onClick={onConfirm} disabled={confirmDisabled} data-testid="handover-confirm-btn">
            <Check className="h-4 w-4 shrink-0 mr-1.5" />
            <span className="truncate">{confirmLabel}</span>
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Custom Handover Dropdown (kein natives <select>, Desktop+Mobile gleich) ──
function HandoverDropdown({ handoverType, selectedGiver, colleagues, onSelect, allowedGiverId, overrideMode }) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);

  // Schließen bei Klick außerhalb
  React.useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const displayLabel = () => {
    if (!handoverType) return '– Bitte auswählen –';
    if (handoverType === 'fruehschicht') return 'Frühschicht';
    if (handoverType === 'colleague' && selectedGiver) return selectedGiver.name;
    return '– Bitte auswählen –';
  };

  const handleSelect = (type, giver = null) => {
    onSelect(type, giver);
    setOpen(false);
  };

  // E2: wählbar ist nur der letzte Melder — außer im Notfall-Modus (Admin/SL)
  const isSelectable = (empId) => overrideMode || empId === allowedGiverId;

  return (
    <div ref={ref} className="relative" data-testid="handover-dropdown-wrapper">
      {/* Trigger */}
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className={`w-full flex items-center justify-between h-10 px-3 rounded-lg border text-sm transition-colors bg-background
          ${open ? 'border-primary ring-2 ring-primary/20' : 'border-border hover:border-primary/60'}
          ${!handoverType ? 'text-muted-foreground' : 'text-foreground font-medium'}`}
        data-testid="handover-dropdown-trigger">
        <span className="truncate">{displayLabel()}</span>
        <ChevronDown className={`h-4 w-4 text-muted-foreground shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {/* Overlay-Liste */}
      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-xl border border-border bg-background shadow-xl overflow-hidden"
          data-testid="handover-dropdown-menu">
          {/* Gruppe: Schicht */}
          <div>
            <p className="px-3 py-1.5 text-[10px] font-bold text-muted-foreground uppercase tracking-widest bg-muted/50 border-b border-border/50">
              Schicht
            </p>
            <button
              type="button"
              onClick={() => handleSelect('fruehschicht')}
              className="w-full flex items-center gap-2 px-3 py-2.5 text-sm hover:bg-muted/60 transition-colors text-left"
              data-testid="handover-fruehschicht-option">
              <Sunrise className="h-4 w-4 text-amber-500 shrink-0" />
              <span className="flex-1">Frühschicht</span>
              {handoverType === 'fruehschicht' && <Check className="h-4 w-4 text-primary shrink-0" />}
            </button>
          </div>

          {/* Gruppe: Mitarbeiter */}
          {colleagues.length > 0 && (
            <div>
              <p className="px-3 py-1.5 text-[10px] font-bold text-muted-foreground uppercase tracking-widest bg-muted/50 border-y border-border/50">
                {overrideMode ? 'Mitarbeiter (Notfall – alle wählbar)' : 'Letzter Melder'}
              </p>
              <div className="max-h-48 overflow-y-auto">
                {colleagues.map(emp => {
                  const selectable = isSelectable(emp.id);
                  const isLast = emp.id === allowedGiverId;
                  return (
                    <button
                      key={emp.id}
                      type="button"
                      disabled={!selectable}
                      onClick={selectable ? () => handleSelect('colleague', { id: emp.id, name: emp.name }) : undefined}
                      className={`w-full flex items-center gap-2 px-3 py-2.5 text-sm text-left transition-colors
                        ${selectable ? 'hover:bg-muted/60' : 'opacity-40 cursor-not-allowed'}`}
                      data-testid={`handover-colleague-${emp.id}`}>
                      <User className="h-4 w-4 text-muted-foreground shrink-0" />
                      <span className="flex-1">{emp.name}</span>
                      {isLast && !overrideMode && (
                        <span className="text-[10px] font-semibold text-blue-600 bg-blue-100 dark:bg-blue-900/40 rounded px-1.5 py-0.5 shrink-0">
                          letzter Melder
                        </span>
                      )}
                      {selectedGiver?.id === emp.id && <Check className="h-4 w-4 text-primary shrink-0" />}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function ZusatzReportingPanel() {
  const { user } = useAuth();
  const [products, setProducts]               = useState([]);
  const [newStocks, setNewStocks]             = useState({});
  const [originalStocks, setOriginalStocks]   = useState({});
  const [loading, setLoading]                 = useState(true);
  const [saving, setSaving]                   = useState(false);
  const [colleagues, setColleagues]           = useState([]);
  const [pendingHandovers, setPendingHandovers] = useState([]);

  // Übergabe-Flow States (Dropdown jetzt inline, nicht als Modal)
  const [handoverType, setHandoverType]   = useState('');           // '' = noch nichts gewählt
  const [selectedGiver, setSelectedGiver] = useState(null);
  const [showStep1, setShowStep1]         = useState(false);
  const [showStep2, setShowStep2]         = useState(false);
  const [showFruehschicht, setShowFruehschicht] = useState(false);
  const [currentHandoverId, setCurrentHandoverId] = useState(null);
  const [currentHandoverData, setCurrentHandoverData] = useState(null);
  const [fruehschichtDiscrepancy, setFruehschichtDiscrepancy] = useState(false);
  const [fruehschichtNote, setFruehschichtNote] = useState('');
  const [showNoSale, setShowNoSale] = useState(false);
  const [currentBatchId, setCurrentBatchId] = useState(null);
  const [suggestion, setSuggestion] = useState(null);      // E2: Vorgänger-Vorschlag
  const [overrideMode, setOverrideMode] = useState(false); // E2: Admin/SL Notfallwahl
  const [recentWarning, setRecentWarning] = useState(null); // E3: Doppelmeldungs-Hinweis
  const [locations, setLocations] = useState([]);           // E4: Standorte des Nutzers
  const [selectedLocation, setSelectedLocation] = useState(''); // E4: gewählter Standort ('' = alle)
  const batchIdRef = React.useRef(null);
  const corrIdRef = React.useRef(null);                     // E3: stabiler Schlüssel für Korrektur

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const locParam = selectedLocation ? `?location_id=${encodeURIComponent(selectedLocation)}` : '';
      const [prodRes, pendRes] = await Promise.all([
        axios.get(`${API}/zusatz/products/reporting${locParam}`, { headers: authH() }),
        axios.get(`${API}/zusatz/handovers/pending`, { headers: authH() }),
      ]);
      const prods = prodRes.data || [];
      setProducts(prods);
      const stocks = {}, origStocks = {};
      prods.forEach(p => { stocks[p.id] = p.stock; origStocks[p.id] = p.stock; });
      setNewStocks(stocks);
      setOriginalStocks(origStocks);
      setPendingHandovers(pendRes.data || []);
      // Kollegen-Liste: /user/colleagues ist für alle Rollen zugänglich (employee, SL, admin)
      try {
        const empRes = await axios.get(`${API}/user/colleagues`, { headers: authH() });
        setColleagues(empRes.data || []);
      } catch {}
      // E2: Vorgänger-Vorschlag + Eingrenzung laden und vorauswählen
      try {
        const sugRes = await axios.get(`${API}/zusatz/handover/suggestion${selectedLocation ? `?location_id=${encodeURIComponent(selectedLocation)}` : ''}`, { headers: authH() });
        const sug = sugRes.data;
        setSuggestion(sug);
        setOverrideMode(false);
        if (sug?.suggested_type === 'colleague' && sug?.suggested_giver) {
          setHandoverType('colleague');
          setSelectedGiver({ id: sug.suggested_giver.id, name: sug.suggested_giver.name });
        } else {
          setHandoverType('fruehschicht');
          setSelectedGiver(null);
        }
      } catch {}
    } catch { toast.error('Fehler beim Laden'); }
    finally { setLoading(false); }
  }, [user?.id, selectedLocation]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // E4: Standorte des Nutzers laden; bei genau einem Standort automatisch wählen
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/user/locations`, { headers: authH() });
        const locs = res.data || [];
        setLocations(locs);
        if (locs.length === 1) setSelectedLocation(locs[0].id);
      } catch {}
    })();
  }, []);

  const adj = (pid, delta) => {
    const max = originalStocks[pid] || 0;
    setNewStocks(prev => ({ ...prev, [pid]: Math.min(max, Math.max(0, (prev[pid] || 0) + delta)) }));
  };

  const getDiff = (pid) => (newStocks[pid] || 0) - (products.find(p => p.id === pid)?.stock || 0);
  const hasSales = () => Object.keys(newStocks).some(pid => getDiff(pid) < 0);
  const hasChanges = () => Object.keys(newStocks).some(pid => getDiff(pid) !== 0);

  const getStatus = (product) => {
    const s = newStocks[product.id] || 0;
    const t = product.warning_threshold;
    if (s === 0) return '⚫';
    if (s < t) return '🔴';
    if (s === t) return '🟡';
    return '🟢';
  };

  const totalExpectedCash = products.reduce((sum, p) => {
    const sold = Math.max(0, p.stock - (newStocks[p.id] ?? p.stock));
    return sum + sold * p.price;
  }, 0);

  const handleSaveClick = () => handleHandoverConfirm();

  const handleHandoverConfirm = async () => {
    if (saving) return;                       // Doppelklick-Sperre
    if (!handoverType) { toast.error('Bitte "Schicht übernommen von" auswählen'); return; }
    if (handoverType === 'colleague' && !selectedGiver) { toast.error('Bitte Kollegen auswählen'); return; }
    // Frühschicht: erst Bestätigungs-Fenster (optional Abweichung), dann senden
    if (handoverType === 'fruehschicht') { setShowFruehschicht(true); return; }
    submitBatch();
  };

  const submitBatch = async () => {
    if (saving) return;
    setSaving(true);
    try {
      // Idempotente Melde-Kennung: einmal pro Meldeversuch erzeugen, bei Retry wiederverwenden
      if (!batchIdRef.current) batchIdRef.current = (window.crypto?.randomUUID?.() || String(Date.now()) + Math.random());
      const items = products.map(p => ({ product_id: p.id, new_stock: newStocks[p.id] ?? p.stock }));
      const r = await axios.post(`${API}/zusatz/report-batch`, {
        client_batch_id: batchIdRef.current,
        handover_type: handoverType,
        giver_user_id: handoverType === 'colleague' ? selectedGiver?.id : null,
        giver_user_name: handoverType === 'colleague' ? selectedGiver?.name : null,
        discrepancy_note: handoverType === 'fruehschicht' ? (fruehschichtNote || null) : null,
        override_giver: handoverType === 'colleague' ? overrideMode : false,
        location_id: selectedLocation || null,
        items,
      }, { headers: authH() });

      const data = r.data;

      // E3: Doppelmeldungs-Hinweis (eigene Meldung < 15 Min.) → Fenster zeigen, nichts gespeichert
      if (data.status === 'recent_report_warning') {
        setRecentWarning({ ...data.recent, window_minutes: data.window_minutes });
        setSaving(false);
        return;   // batchIdRef bewusst NICHT zurücksetzen (Nutzer entscheidet im Fenster)
      }

      setCurrentBatchId(data.batch_id);
      setCurrentHandoverData({
        giver_user_name: selectedGiver?.name,
        total_amount: data.total_amount,
        total_sold: data.total_sold,
        items: data.items || [],
      });
      batchIdRef.current = null;              // für nächste Meldung frischen Schlüssel erzeugen
      setShowFruehschicht(false);

      if (data.status === 'completed_no_sale') { setShowNoSale(true); setSaving(false); return; }
      if (data.status === 'completed_fruehschicht') {
        toast.success('Schicht gespeichert – Frühschicht bestätigt');
        if (data.total_sold > 0) toast.info(`Abendverkauf erfasst: ${data.total_amount?.toFixed(2)} €`);
        setFruehschichtDiscrepancy(false); setFruehschichtNote('');
        fetchData(); setSaving(false); return;
      }
      // Fall B: Kollege + Verkauf → Schritt 1
      setShowStep1(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Melden');
    } finally { setSaving(false); }
  };

  const handleFruehschichtConfirm = async () => {
    await submitBatch();
  };

  // E3: "Meldung stimmt, abbrechen" – nichts speichern, Fenster schließen
  const handleKeepPrevious = () => {
    setRecentWarning(null);
    batchIdRef.current = null;   // nächster Versuch bekommt frischen Schlüssel
    setSaving(false);
  };

  // E3: "Bestand korrigieren" – ersetzt die eigene letzte Meldung (correct_batch_id)
  const handleCorrectStock = async () => {
    if (saving || !recentWarning) return;
    setSaving(true);
    try {
      if (!corrIdRef.current) corrIdRef.current = (window.crypto?.randomUUID?.() || String(Date.now()) + Math.random());
      const items = products.map(p => ({ product_id: p.id, new_stock: newStocks[p.id] ?? p.stock }));
      const r = await axios.post(`${API}/zusatz/report-batch`, {
        client_batch_id: corrIdRef.current,
        handover_type: handoverType || 'fruehschicht',
        giver_user_id: handoverType === 'colleague' ? selectedGiver?.id : null,
        giver_user_name: handoverType === 'colleague' ? selectedGiver?.name : null,
        override_giver: false,
        correct_batch_id: recentWarning.batch_id,
        location_id: selectedLocation || null,
        items,
      }, { headers: authH() });
      const data = r.data;
      if (data.status === 'corrected' || data.status === 'completed_correction') {
        const hint = data.old_handover_result === 'kept'
          ? ' (vorherige Übergabe war bereits bestätigt und bleibt unverändert)'
          : '';
        toast.success(`Bestand korrigiert${hint}`);
      } else {
        toast.success('Bestand korrigiert');
      }
      setRecentWarning(null);
      batchIdRef.current = null;
      corrIdRef.current = null;
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler bei der Korrektur');
    } finally { setSaving(false); }
  };

  const handleStep1Confirm = async () => {
    if (!currentBatchId) return;
    try {
      await axios.post(`${API}/zusatz/report-batch/${currentBatchId}/confirm-giver`, {}, { headers: authH() });
      setShowStep1(false); setShowStep2(true);
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
  };

  const handleStep2Confirm = async () => {
    if (!currentBatchId) return;
    try {
      await axios.post(`${API}/zusatz/report-batch/${currentBatchId}/confirm-receiver`, {}, { headers: authH() });
      setShowStep2(false);
      toast.success('Übergabe vollständig bestätigt – Geld im Tresor');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
  };

  const handleNoSaleConfirm = () => {
    setShowNoSale(false);
    toast.success('Bestätigt – kein Verkauf seit letzter Meldung');
    fetchData();
  };

  const handleConfirmPending = async (ho) => {
    try {
      if (ho.status === 'pending_giver_confirmation') {
        await axios.post(`${API}/zusatz/handovers/${ho.id}/confirm-giver`, {}, { headers: authH() });
        toast.success('Übergabe-Bestätigung gesendet');
      } else {
        await axios.post(`${API}/zusatz/handovers/${ho.id}/confirm-receiver`, {}, { headers: authH() });
        toast.success('Empfang bestätigt');
      }
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-16">
      <Loader2 className="h-10 w-10 animate-spin text-primary" />
    </div>
  );

  return (
    <div className="space-y-4 pb-24" data-testid="zusatz-reporting-panel">
      {/* E4: Standort-Auswahl (nur bei mehreren Standorten) */}
      {locations.length > 1 && (
        <Card className="border-blue-200 bg-blue-50/50 dark:bg-blue-900/10">
          <CardContent className="py-3 flex items-center gap-3">
            <label htmlFor="zusatz-location" className="text-sm font-medium whitespace-nowrap">Standort</label>
            <select
              id="zusatz-location"
              data-testid="zusatz-location-select"
              value={selectedLocation}
              onChange={(e) => setSelectedLocation(e.target.value)}
              className="flex-1 h-9 rounded-lg border border-input bg-background px-3 text-sm"
            >
              <option value="">Alle Standorte</option>
              {locations.map(l => (
                <option key={l.id} value={l.id}>{l.name}</option>
              ))}
            </select>
          </CardContent>
        </Card>
      )}

      {/* Ausstehende Bestätigungen */}
      {pendingHandovers.length > 0 && (
        <Card className="border-amber-300 bg-amber-50/60 dark:bg-amber-900/10">
          <CardHeader className="pb-2 pt-3">
            <CardTitle className="text-sm flex items-center gap-2 text-amber-700 dark:text-amber-400">
              <Clock className="h-4 w-4" /> Ausstehende Übergabe-Bestätigungen ({pendingHandovers.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 pb-3">
            {pendingHandovers.map(ho => (
              <div key={ho.id} className="flex items-center gap-2 bg-white dark:bg-background rounded-xl border border-amber-200 px-3 py-2">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">{ho.product_name}</p>
                  <p className="text-xs text-muted-foreground">
                    {ho.status === 'pending_giver_confirmation'
                      ? `Du hast ${ho.sold} Stück = ${ho.expected_amount?.toFixed(2)} € übergeben?`
                      : `Du erhältst ${ho.expected_amount?.toFixed(2)} € von ${ho.giver_user_name}?`}
                  </p>
                </div>
                <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white shrink-0"
                  onClick={() => handleConfirmPending(ho)} data-testid={`confirm-pending-${ho.id}`}>
                  <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Bestätigen
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Produktliste */}
      {products.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <PackagePlus className="h-12 w-12 mx-auto mb-3 opacity-40" />
          <p className="font-medium">Keine Produkte für Bestandsmeldung freigegeben</p>
          <p className="text-sm mt-1">Im Tab "Produktverwaltung" die MA-Freigabe aktivieren</p>
        </div>
      ) : (
        <Card>
          <CardHeader className="bg-gradient-to-r from-blue-500 to-blue-600 text-white py-2.5 rounded-t-xl">
            <CardTitle className="flex items-center gap-2 text-base">
              <PackagePlus className="h-4 w-4" /> Bestand zählen
              <Badge variant="secondary" className="ml-auto bg-blue-100 text-blue-700 text-xs">{products.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0 divide-y">
            {products.map(product => {
              const diff = getDiff(product.id);
              const cur  = newStocks[product.id] ?? product.stock;
              return (
                <div key={product.id} className={`p-3 transition-colors ${diff < 0 ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}
                  data-testid={`zusatz-product-${product.id}`}>
                  <div className="flex items-center gap-2">
                    <span className="text-lg shrink-0">{getStatus(product)}</span>
                    <div className="flex-1 min-w-0">
                      <span className="font-semibold text-sm">{product.name}</span>
                      <span className="text-xs text-muted-foreground ml-2">{product.price.toFixed(2)} €</span>
                      {diff < 0 && <span className="text-xs text-red-600 font-bold ml-2">{diff}</span>}
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <Button variant="outline" size="sm" onClick={() => adj(product.id, -1)} disabled={cur === 0}
                        className="h-9 w-9 p-0 text-red-600 disabled:opacity-40">
                        <Minus className="h-4 w-4" />
                      </Button>
                      <div className={`w-12 h-9 flex items-center justify-center rounded-lg border font-bold text-base
                        ${cur > 0 && cur < product.warning_threshold ? 'bg-red-100 border-red-400 text-red-700' :
                          cur > 0 ? 'bg-blue-100 border-blue-400 text-blue-700' :
                          'bg-gray-50 border-gray-300 text-gray-400'}`}>
                        {cur}
                      </div>
                      <Button variant="outline" size="sm" onClick={() => adj(product.id, 1)} disabled={cur >= originalStocks[product.id]}
                        className="h-9 w-9 p-0 text-green-600 disabled:opacity-40">
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* ── INLINE: "SCHICHT ÜBERNOMMEN VON *" — kompaktes Custom-Dropdown ── */}
      {products.length > 0 && (
        <div className="space-y-1">
          <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
            SCHICHT ÜBERNOMMEN VON <span className="text-red-500">*</span>
          </label>
          <HandoverDropdown
            handoverType={handoverType}
            selectedGiver={selectedGiver}
            colleagues={colleagues}
            allowedGiverId={suggestion?.last_reporter?.id || null}
            overrideMode={overrideMode}
            onSelect={(type, giver) => {
              setHandoverType(type);
              setSelectedGiver(giver);
            }}
          />
          {/* E2: Vorauswahl-Hinweis */}
          {suggestion && (
            <p className="text-xs text-muted-foreground/80 mt-0.5" data-testid="handover-suggestion-hint">
              {suggestion.suggested_type === 'colleague' && suggestion.suggested_giver
                ? <>Vorausgewählt: <strong>{suggestion.suggested_giver.name}</strong> (letzter Melder heute)</>
                : <>Erste Meldung heute – vorausgewählt: <strong>Frühschicht</strong></>}
            </p>
          )}
          {/* E2: Notfall-Override nur für Admin/Stationsleitung */}
          {suggestion?.can_override && (
            <button
              type="button"
              onClick={() => {
                const next = !overrideMode;
                setOverrideMode(next);
                // Beim Zurückschalten auf Normalmodus: ungültige Auswahl zurücksetzen
                if (!next && handoverType === 'colleague' && selectedGiver?.id !== (suggestion?.last_reporter?.id || null)) {
                  if (suggestion?.last_reporter) { setSelectedGiver({ id: suggestion.last_reporter.id, name: suggestion.last_reporter.name }); }
                  else { setHandoverType('fruehschicht'); setSelectedGiver(null); }
                }
              }}
              className={`mt-1 inline-flex items-center gap-1.5 text-xs font-medium rounded-full px-3 py-1 border transition-colors
                ${overrideMode
                  ? 'bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-900/30 dark:text-amber-300'
                  : 'bg-muted/40 text-muted-foreground border-border hover:border-amber-300'}`}
              data-testid="handover-override-toggle">
              <AlertTriangle className="h-3.5 w-3.5" />
              {overrideMode ? 'Notfall-Modus aktiv – jede Person wählbar' : 'Notfall: andere Person wählen (Admin/SL)'}
            </button>
          )}
          {!handoverType && (
            <p className="text-xs text-muted-foreground/70 italic mt-0.5">
              Pflichtfeld — Auswahl aktiviert den Speichern-Button
            </p>
          )}
          {/* Betrag-Vorschau wenn Verkäufe und Auswahl getroffen */}
          {handoverType && hasSales() && (
            <div className="mt-2 bg-muted/40 rounded-lg px-3 py-2 text-sm space-y-0.5">
              {products.filter(p => getDiff(p.id) < 0).map(p => {
                const sold = Math.abs(getDiff(p.id));
                return (
                  <div key={p.id} className="flex justify-between">
                    <span className="text-muted-foreground">{p.name}: {sold} Stück</span>
                    <span className="font-semibold">{(sold * p.price).toFixed(2)} €</span>
                  </div>
                );
              })}
              <div className="border-t border-border pt-1 flex justify-between font-bold text-primary">
                <span>Gesamt erwartet</span>
                <span>{totalExpectedCash.toFixed(2)} €</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Speichern-Button — disabled solange keine Auswahl */}
      {products.length > 0 && (
        <div className="flex justify-center">
          <Button onClick={handleSaveClick}
            disabled={saving || !handoverType || (handoverType === 'colleague' && !selectedGiver)}
            size="lg"
            className={`h-12 px-8 text-white rounded-full gap-2 shadow-lg transition-all ${
              handoverType && !(handoverType === 'colleague' && !selectedGiver)
                ? hasChanges() ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-500 hover:bg-gray-600'
                : 'bg-gray-300 cursor-not-allowed'
            }`}
            data-testid="zusatz-save-report-btn">
            {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <Save className="h-5 w-5" />}
            {saving ? 'Speichere…' : hasChanges() ? 'Schicht speichern' : 'Bestand bestätigen'}
          </Button>
        </div>
      )}


      {/* ── Modal A: Frühschicht ── */}
      {showFruehschicht && (
        <StepModal title="Frühschicht – Bestandsbestätigung" icon={Sunrise} iconColor="text-amber-500"
          confirmLabel="Frühschicht bestätigen" onConfirm={handleFruehschichtConfirm}
          onCancel={() => setShowFruehschicht(false)}>
          <div className="space-y-3">
            {hasSales() && (
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 rounded-xl px-4 py-4">
                <p className="text-sm text-muted-foreground">Erwartete Einnahmen</p>
                <p className="text-2xl font-bold text-blue-700">{totalExpectedCash.toFixed(2)} €</p>
                <p className="text-xs text-muted-foreground">bitte in den Tresor legen</p>
              </div>
            )}
            {!hasSales() && (
              <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3 text-sm text-green-700">
                Kein Verkauf – Bestand unverändert
              </div>
            )}
            <div>
              <label className="text-xs font-medium text-muted-foreground">Abweichung? (optional)</label>
              <input type="text" className="w-full mt-1 h-9 rounded-lg border border-border bg-background px-3 text-sm"
                placeholder="z.B. 3 Stück fehlen" value={fruehschichtNote}
                onChange={e => { setFruehschichtNote(e.target.value); setFruehschichtDiscrepancy(!!e.target.value.trim()); }} />
              {fruehschichtDiscrepancy && (
                <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" /> Admin wird benachrichtigt
                </p>
              )}
            </div>
          </div>
        </StepModal>
      )}

      {/* ── Modal B1: Geber (Summe + Artikelliste) ── */}
      {showStep1 && currentHandoverData && (
        <StepModal title={`${selectedGiver?.name} bestätigt Übergabe`} subtitle="Schritt 1 von 2"
          icon={User} iconColor="text-blue-600"
          confirmLabel={`Ich übergebe ${(currentHandoverData.total_amount ?? 0).toFixed(2)} €`}
          onConfirm={handleStep1Confirm} onCancel={() => setShowStep1(false)}>
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 rounded-xl px-4 py-4" data-testid="handover-summary-box">
            <p className="text-sm text-muted-foreground">Übergabe an <strong>{user?.name}</strong></p>
            <p className="text-2xl font-bold text-blue-700" data-testid="handover-total-amount">{(currentHandoverData.total_amount ?? 0).toFixed(2)} €</p>
            <div className="mt-2 space-y-1">
              {(currentHandoverData.items || []).map((it, i) => (
                <div key={i} className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-300" data-testid={`handover-item-${i}`}>
                  <span>{it.sold}× {it.product_name}</span>
                  <span className="font-medium">{it.amount?.toFixed(2)} €</span>
                </div>
              ))}
            </div>
          </div>
          <p className="text-xs text-muted-foreground text-center">{selectedGiver?.name} tippt auf Bestätigen</p>
        </StepModal>
      )}

      {/* ── Modal B2: Empfänger legt Geld in den Tresor ── */}
      {showStep2 && currentHandoverData && (
        <StepModal title="Geld in den Tresor legen" subtitle="Schritt 2 von 2"
          icon={Euro} iconColor="text-green-600"
          confirmLabel="Ich habe das Geld in den Tresor gelegt"
          onConfirm={handleStep2Confirm} onCancel={() => setShowStep2(false)}>
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 rounded-xl px-4 py-4">
            <p className="text-sm text-muted-foreground">Von <strong>{selectedGiver?.name}</strong> erhalten</p>
            <p className="text-2xl font-bold text-green-700">{(currentHandoverData.total_amount ?? 0).toFixed(2)} €</p>
            <p className="text-xs text-muted-foreground">Bitte in den Tresor legen und bestätigen.</p>
          </div>
        </StepModal>
      )}

      {/* ── Modal C: Nichts verkauft (1 Klick) ── */}
      {showNoSale && (
        <StepModal title="Keine Verkäufe" icon={Check} iconColor="text-green-600"
          confirmLabel="Verstanden"
          onConfirm={handleNoSaleConfirm} onCancel={handleNoSaleConfirm}>
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 rounded-xl px-4 py-4 text-center" data-testid="no-sale-box">
            <p className="text-sm text-gray-700 dark:text-gray-200">
              {selectedGiver?.name ? <><strong>{selectedGiver.name}</strong> hat nichts verkauft.</> : 'Kein Verkauf seit der letzten Meldung.'}
            </p>
            <p className="text-xs text-muted-foreground mt-1">Bestand unverändert – wird gespeichert.</p>
          </div>
        </StepModal>
      )}

      {/* ── E3: Doppelmeldungs-Hinweis / Korrektur ── */}
      {recentWarning && (
        <div className="fixed inset-0 z-[9999] bg-black/60 flex items-center justify-center p-4"
             onClick={handleKeepPrevious} data-testid="recent-report-dialog">
          <div className="bg-background rounded-2xl shadow-2xl max-w-md w-full p-6 space-y-4"
               onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center shrink-0">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <h3 className="font-bold text-base">Du hast gerade schon gemeldet</h3>
                <p className="text-sm text-muted-foreground" data-testid="recent-report-minutes">
                  Vor {recentWarning.minutes_ago} {recentWarning.minutes_ago === 1 ? 'Minute' : 'Minuten'} gemeldet
                  {' '}(innerhalb von {recentWarning.window_minutes} Min.)
                </p>
              </div>
            </div>

            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 rounded-xl px-4 py-3 space-y-1.5"
                 data-testid="recent-report-values">
              <p className="text-sm font-medium">Deine letzte Meldung:</p>
              {(recentWarning.items && recentWarning.items.length > 0) ? (
                recentWarning.items.map((it, i) => (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <span>{it.sold}× {it.product_name}</span>
                    <span className="font-medium">{(it.amount ?? 0).toFixed(2)} €</span>
                  </div>
                ))
              ) : (
                <p className="text-sm text-muted-foreground">Kein Verkauf gemeldet</p>
              )}
              <div className="flex items-center justify-between text-sm pt-1 border-t border-amber-200 mt-1">
                <span className="font-semibold">Summe</span>
                <span className="font-bold">{(recentWarning.total_amount ?? 0).toFixed(2)} €</span>
              </div>
            </div>

            <p className="text-xs text-muted-foreground">
              Wenn die vorherige Meldung stimmt, brich ab. Andernfalls kannst du den Bestand
              korrigieren – deine letzte Meldung wird ersetzt (kein zusätzlicher Verkauf).
            </p>

            <div className="flex flex-col gap-2 pt-1">
              <Button onClick={handleKeepPrevious} variant="outline" className="w-full"
                      data-testid="recent-keep-btn" disabled={saving}>
                <Check className="h-4 w-4 mr-1.5" /> Meldung stimmt, abbrechen
              </Button>
              <Button onClick={handleCorrectStock} className="w-full"
                      data-testid="recent-correct-btn" disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <ArrowRightLeft className="h-4 w-4 mr-1.5" />}
                Bestand korrigieren
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
